import {HoistModel, persist, SelectOption, TaskObserver, XH} from '@xh/hoist/core';
import {action, bindable, observable} from '@xh/hoist/mobx';
import {WatchListUpdate, WatchListUpdates} from '../../data/DataTypes';
import {range} from 'lodash';
import {NotificationMessage} from '../../data/MessageTypes';

export class TickerTapeModel extends HoistModel {
    apiTask = TaskObserver.trackLast();
    private dataItems = new Map<string, WatchListUpdate>();
    @observable accessor copyCount = 1;
    @observable accessor dataMarkup: string;
    override persistWith = {prefKey: 'tickerTapeState'};
    @bindable @persist accessor watchlistId: number;
    @bindable @persist accessor scrollSpeedFactor: 1.0;
    @bindable @persist accessor textSize: 'unset';

    constructor() {
        super();

        if (!this.isEnabled) return;

        this.addReaction({
            track: () => XH.viewportSize,
            run: () => this.setCopyCount()
        });

        this.addReaction({
            track: () => this.watchlistId,
            run: async () => {
                await this.setUpTickers().linkTo(this.apiTask);
            },
            fireImmediately: true
        });

        this.addReaction({
            track: () => this.scrollSpeedFactor,
            run: () => this.buildMarqueeData()
        });

        XH.messageHub.subscribe('watchlist', this.xhId, message => this.onWatchlistUpdate(message));
    }

    get isEnabled(): boolean {
        return XH.getPref('showTickerTape');
    }

    get needsScroll(): boolean {
        return this.copyCount > 1;
    }

    get watchlists(): SelectOption[] {
        return [{label: 'Default', value: XH.watchListService.defaultWatchlistId}, ...XH.watchListService.watchLists];
    }

    get speeds(): SelectOption[] {
        return [
            {label: 'Slow', value: 0.5},
            {label: 'Slower', value: 0.75},
            {label: 'Normal', value: 1},
            {label: 'Faster', value: 1.25}
        ];
    }

    get textSizes(): SelectOption[] {
        return [
            {label: 'Normal', value: 'unset'},
            {label: 'Larger', value: 'larger'},
            {label: 'Medium', value: 'medium'},
            {label: 'Large', value: 'large'}
        ];
    }

    private async setUpTickers() {
        console.log('hooking tickertape to watchlist', this.watchlistId);
        if (!this.watchlistId) {
            const {defaultWatchlistId} = XH.watchListService;
            if (!defaultWatchlistId) {
                XH.handleException('default watchlist is not set up');
                return;
            }
            this.watchlistId = defaultWatchlistId;
        }

        const watchList = await XH.watchListService.subscribeWatchlistAsync(this.watchlistId);
        this.dataItems = new Map(watchList.updates.map(u => [u.ticker, u]));

        this.setCopyCount();
        this.buildMarqueeData();
    }

    private onWatchlistUpdate(message: NotificationMessage) {
        switch (message.envelope.eventType) {
            case 'UPDATE': {
                const watchlistUpdates = message.payload as WatchListUpdates;
                if (watchlistUpdates.watchlistId != this.watchlistId) return;
                this.updateTickers(watchlistUpdates.updates);
                break;
            }
            case 'RELOAD':
                this.setUpTickers();
                break;
        }
    }

    private async updateTickers(updates: WatchListUpdate[]) {
        updates.forEach(u => {
            this.dataItems.set(u.ticker, u);
        });

        this.buildMarqueeData();
    }

    @action private setCopyCount() {
        const size = XH.viewportSize,
            itemLength = 200,
            totalLength = this.dataItems.size * itemLength;
        if (totalLength < size.width) {
            this.copyCount = 1;
        } else {
            this.copyCount = Math.ceil(size.width / totalLength) + 1;
        }

        this.buildMarqueeData();
    }

    @action private buildMarqueeData() {
        const html = range(this.copyCount)
            .map(() => this.getMarqueeSegment())
            .join('\n');
        this.dataMarkup = html;
    }

    private getMarqueeSegment() {
        const tickers = Array.from(this.dataItems.values()),
            duration = this.copyCount > 1 ? (this.dataItems.size * 1.5) / this.scrollSpeedFactor : 0,
            style = `style="white-space: nowrap; max-width: none; animation: marquee ${duration}s linear infinite;"`;
        return `<span ${style}>${tickers.map(ticker => this.getStockMarkup(ticker)).join('\n\t')}</span>`;
    }

    private getStockMarkup(ticker: WatchListUpdate) {
        const upDown = ticker.priceChangePct > 0 ? 'up' : ticker.priceChange < 0 ? 'down' : '',
            prefix = ticker.priceChangePct >= 0 ? '+' : '',
            price = '$' + ticker.price.toFixed(2),
            change = (ticker.priceChangePct * 100).toFixed(2) + '%';
        return `
        <span class="stock-ticker ${upDown}">
            <span class="stock-ticker-symbol">${ticker.ticker}</span>
            <span id="tiker-${ticker.ticker}-price" class="stock-ticker-price">${price}</span>
            <span id="ticker-${ticker.ticker}-change" class="stock-ticker-change">${prefix}${change}</span>
        </span>
        `;
    }
}
